/**
 * SL-15 — the ONE-TRUTH 3D geometry math for exploding an ELLIPSE child out of a
 * block INSERT.
 *
 * Both explode paths call THIS function so the optimistic shell-preview and the
 * reconciled persisted document are BYTE-IDENTICAL (shell↔doc parity — a
 * load-bearing matrix invariant):
 *   - the persisted-document reducer
 *     (apps/web/src/app/state/blockReducerHelpers.ts `explodeChild` Ellipse arm)
 *     adapts the result into a typed `DrawingEntity`;
 *   - the transient shell-preview mutation
 *     (packages/command-engine/src/blockMutations.ts `explodeChildEntity` Ellipse
 *     arm) adapts the result into the `EntityPreview` property bag.
 *
 * This helper returns PLAIN geometry (points / ratio / OCS normal). Each caller
 * adapts to its own entity shape — NO CAD semantics leak into either adapter, and
 * neither caller re-derives the math (which would guarantee re-divergence).
 *
 * The INSERT linear part is a 2D affine on XY (`ExplodeAffineMatrix`,
 * `composeInsertMatrix` = T(insertion)·R(+rotDeg)·S(sx,sy)·T(-basePoint)) plus a
 * pure `scaleZ` on z (rotation is about Z; array row/col spacing is XY-only; the
 * 2D matrix never touches z). The fields below correspond to the SL-15 conformance
 * arms:
 *   - F3 — major-axis DIRECTION z rides through as `z · scaleZ` (kept CONDITIONAL
 *     on a present source z so a planar ellipse stays byte-identical, no spurious
 *     `z: 0`).
 *   - F4 + F7 — center POSITION world z = insertionZ + scaleZ·(center.z − basePointZ);
 *     emitted when the INSERT contributes elevation OR the source child carried a z.
 *   - F5 + F1(round-7) — proper affine-ellipse PRINCIPAL axes. Build the TRUE
 *     in-plane minor axis (extrusion × major, length ratio·|major|), transform
 *     BOTH axes by the full 3D linear part → the images `major′`, `minor′` are
 *     CONJUGATE semi-diameters of the resulting ellipse. DXF/AutoCAD stores an
 *     ellipse by its PRINCIPAL major axis + minor/major ratio, so:
 *       · when `major̂′·minor̂′ ≈ 0` (uniform scale — conformal — or an
 *         axis-aligned major axis) the images are ALREADY the principal axes:
 *         keep `major′` and ratio = |minor′|/|major′| (BYTE-IDENTICAL to the
 *         pre-round-7 path — no float noise);
 *       · otherwise (a rotated+tilted ellipse under NON-uniform scale) recover
 *         the principal axes via RYTZ's construction from the conjugate pair, and
 *         recompute ratio + the plane normal from the recovered pair.
 *     This is EXACTLY the branch ezdxf 1.4.3 `ConstructionEllipse.transform`
 *     (`math/ellipse.py`) uses: `if abs(new_major̂·new_minor̂) > 1e-6:
 *     rytz_axis_construction(...)` else keep-and-ratio. The former residual
 *     (docs/test-gaps/ellipse-explode-nonuniform-tilt.md) is now RESOLVED for the
 *     axes/ratio; see that doc for the partial-arc param re-anchor note.
 *   - F6 + F8 — OCS extrusion NORMAL through the INSERT linear part. A plane normal
 *     transforms as det(M)·M⁻ᵀ·n; threading the block-diagonal blockdiag(L, scaleZ)
 *     collapses to the 2×2 COFACTOR map with a scaleZ factor on XY and det(L) on z:
 *       n' ∝ ( sz·(d·nx − b·ny),  sz·(−c·nx + a·ny),  (a·d − b·c)·nz )  then unit.
 *     This is EXACTLY the normal that stays ⊥ to the transformed major/minor axes,
 *     so the exploded (major, minor, normal) triple round-trips mutually consistent.
 *     A mirroring (negative-det) INSERT materialises the flipped (0,0,−1) plane.
 *     (In the F1 round-7 RYTZ branch the normal is instead recomputed as
 *     normalize(principalMajor × principalMinor) — ezdxf's own choice — which is
 *     the SAME plane and likewise encodes the mirror handedness.)
 *
 * Pure math, NO app-internal imports — a leaf module (no dependency cycle). The
 * only import is the sibling `./vec3` (the ONE monorepo pure-vector-math truth),
 * kept in this same package so nothing app-internal is pulled in.
 */
import { cross, length, normalize, type Vec3 } from "./vec3";

/**
 * 2D affine matrix, SVG column-major (a,b,c,d = the 2×2 linear part L, e,f = the
 * translation). Structurally identical to the reducer's `Mat` and the shell
 * mutation's `BlockMat`, so both callers pass their own matrix verbatim.
 */
export interface ExplodeAffineMatrix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

/** A POSITION / DIRECTION whose z ELEVATION is optional (planar ⇒ z absent). */
interface OptionalZPoint {
  x: number;
  y: number;
  z?: number;
}

/** The source ellipse geometry (a block child's local-frame ellipse). */
export interface EllipseExplodeSource {
  center: OptionalZPoint;
  majorAxisEndpoint: OptionalZPoint; // direction vector relative to center
  ratio: number; // minor/major, (0, 1]
  extrusion: Vec3; // OCS plane normal
  /**
   * Arc start/end parameters (RADIANS, CCW from the major axis). OPTIONAL —
   * omit for a FULL ellipse (the params are rotation-invariant, so the helper
   * returns them unchanged and NEVER re-anchors, keeping a full ellipse
   * byte-identical). When present AND the principal frame ROTATES (the Rytz
   * branch, or the ratio>1 axis swap), the helper re-anchors them to the new
   * major-axis frame (ezdxf `ConstructionEllipse.transform` `adjust_params`).
   */
  startParam?: number;
  endParam?: number;
}

/** The INSERT transform inputs (2D affine on XY + scaleZ / elevation on z). */
export interface EllipseExplodeParams {
  matrix: ExplodeAffineMatrix;
  scaleZ: number;
  insertionZ: number; // parent INSERT insertionPoint.z (0 if absent)
  basePointZ: number; // block definition basePoint.z
}

/** The world-frame exploded ellipse geometry (plain, entity-shape-agnostic). */
export interface EllipseExplodeResult {
  center: OptionalZPoint;
  majorAxisEndpoint: OptionalZPoint;
  ratio: number;
  /** The transformed OCS normal (always computed). */
  extrusion: Vec3;
  /**
   * True when `extrusion` ≈ WCS default (0,0,1) — callers OMIT the group
   * 210/220/230 so a planar child under an orientation-preserving INSERT stays
   * byte-identical (the schema re-applies the default).
   */
  extrusionIsDefault: boolean;
  /**
   * Re-anchored arc start/end params (RADIANS), present ONLY when the source
   * carried params. For a full ellipse OR a case that keeps the frame (uniform /
   * axis-aligned / ratio ≤ 1 with no Rytz) these are BYTE-IDENTICAL to the input
   * (same values). For the Rytz / ratio>1-swap cases they are re-measured in the
   * recovered principal major-axis frame so the exported arc reproduces the SAME
   * world endpoints (F2).
   */
  startParam?: number;
  endParam?: number;
}

/** ≈ (0,0,1) within float noise. */
function isDefaultNormal(n: Vec3): boolean {
  return Math.abs(n.x) < 1e-9 && Math.abs(n.y) < 1e-9 && Math.abs(n.z - 1) < 1e-9;
}

/**
 * Unit(a)·`len` — the ezdxf `Vec3.normalize(length)` semantic (scale a vector to
 * a given length). Returns `fallback` when `a` is (near-)null (ezdxf raises
 * there; the callers below only reach this with a well-conditioned vector, so the
 * fallback is defensive). Built on the shared `length` so the cross/length/
 * normalise truth stays in one place (`./vec3`).
 */
function scaleToLength(a: Vec3, len: number, fallback: Vec3): Vec3 {
  const m = length(a);
  if (m < 1e-15) return fallback;
  const k = len / m;
  return { x: a.x * k, y: a.y * k, z: a.z * k };
}

const ZERO_VEC: Vec3 = { x: 0, y: 0, z: 0 };

const TAU = Math.PI * 2;
const HALF_PI = Math.PI / 2;

/** Dot product a·b. */
function dot3(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

/** Wrap an angle into `[0, 2π)` — ezdxf `x % math.tau` (Python `%` on floats
 *  always returns a non-negative result for a positive modulus). */
function modTau(v: number): number {
  const r = v % TAU;
  return r < 0 ? r + TAU : r;
}

/** ezdxf `math.isclose(a, b, abs_tol=…)` scalar variant (the transform uses an
 *  ABSOLUTE 1e-9 tolerance throughout). */
function isCloseNum(a: number, b: number, absTol = 1e-9): boolean {
  return Math.abs(a - b) <= absTol;
}

/**
 * ezdxf `minor_axis(major, extrusion, ratio)` = `extrusion.cross(major)`
 * renormalised to `|major|·ratio` — the in-plane semi-minor axis of an ellipse
 * given its major axis, plane normal and minor/major ratio. Used to recover the
 * EXACT minor axis (ezdxf transform orthogonal branch) and to build the swapped
 * axes (ratio>1). Degenerate (major ∥ extrusion) falls back to (0,0,0).
 */
function minorAxisFrom(major: Vec3, extrusion: Vec3, ratio: number): Vec3 {
  return scaleToLength(cross(extrusion, major), length(major) * ratio, ZERO_VEC);
}

/** ezdxf `mid_param(start, end)` — midpoint param, wrapping `end` past `start`
 *  by a full turn when it trails (so the midpoint lands INSIDE the CCW arc). */
function midParam(start: number, end: number): number {
  const e = end < start ? end + TAU : end;
  return (start + e) / 2;
}

/**
 * Rytz's axis construction — recover the PRINCIPAL (semi-major / semi-minor) axes
 * of an ellipse from two CONJUGATE semi-diameters `d1`, `d2`. Faithful port of
 * ezdxf 1.4.3 `math/ellipse.py` `rytz_axis_construction` (which "works for 2D/3D
 * vectors"): center is the origin; P′ is `d2` rotated 90° in the ellipse plane;
 * D is the midpoint of P′Q; a circle of radius |D| about D meets line P′Q at A/B,
 * giving the axis directions and lengths. Returns the principal (major, minor)
 * vectors (each carrying its true length) and the minor/major ratio.
 *
 * Reference: en.wikipedia.org/wiki/Rytz%27s_construction (cited by ezdxf).
 */
function rytzAxisConstruction(
  d1: Vec3,
  d2: Vec3,
): { major: Vec3; minor: Vec3; ratio: number } {
  const Q = d1; // vector CQ (conjugate semi-diameter #1)
  // P′ = d2 rotated 90° in the ellipse plane, same length as d2.
  let P1: Vec3;
  if (Math.abs(d1.z) < 1e-9 && Math.abs(d2.z) < 1e-9) {
    // ezdxf `Vec3.orthogonal(ccw=False)` for an xy-plane vector (x,y,0) → (y,−x,0).
    P1 = { x: d2.y, y: -d2.x, z: 0 };
  } else {
    const extrusion = cross(d1, d2);
    P1 = scaleToLength(cross(extrusion, d2), length(d2), d2);
  }
  const D: Vec3 = {
    x: (P1.x + Q.x) / 2,
    y: (P1.y + Q.y) / 2,
    z: (P1.z + Q.z) / 2,
  };
  const radius = Math.hypot(D.x, D.y, D.z);
  const radiusVector = scaleToLength(
    { x: Q.x - P1.x, y: Q.y - P1.y, z: Q.z - P1.z },
    radius,
    ZERO_VEC,
  );
  const A: Vec3 = {
    x: D.x - radiusVector.x,
    y: D.y - radiusVector.y,
    z: D.z - radiusVector.z,
  };
  const B: Vec3 = {
    x: D.x + radiusVector.x,
    y: D.y + radiusVector.y,
    z: D.z + radiusVector.z,
  };
  // ezdxf: major length = |A − Q| with DIRECTION of B; minor length = |B − Q|
  // with DIRECTION of A (the axes cross A/B — this is the construction, not a bug).
  const majorLen = Math.hypot(A.x - Q.x, A.y - Q.y, A.z - Q.z);
  const minorLen = Math.hypot(B.x - Q.x, B.y - Q.y, B.z - Q.z);
  return {
    major: scaleToLength(B, majorLen, ZERO_VEC),
    minor: scaleToLength(A, minorLen, ZERO_VEC),
    ratio: majorLen < 1e-15 ? 1 : minorLen / majorLen,
  };
}

/**
 * Transform a source ellipse through the INSERT linear part, returning the
 * world-frame center / major axis / ratio / extrusion normal. See the module
 * doc for the per-field (F3..F8) derivation. Byte-identical for both explode
 * callers by construction.
 */
export function computeExplodedEllipseGeometry(
  source: EllipseExplodeSource,
  params: EllipseExplodeParams,
): EllipseExplodeResult {
  const { center, majorAxisEndpoint, ratio } = source;
  // SL-15 round-10 @codex F1 (P1) — default a MISSING OCS normal to (0,0,1).
  // `EllipseExplodeSource.extrusion` is typed non-optional, but a promotion-built
  // block child (Circle/Arc → Ellipse) reaches EXPLODE with `extrusion` genuinely
  // `undefined` at runtime: `assembleDrawingDocument`'s PROD fast-path skips the Zod
  // default. The minor-axis cross-product below reads `.x/.y/.z` off it, so an
  // unguarded read throws → the whole explode crashes. Defaulting here keeps this
  // ONE-TRUTH helper safe for BOTH explode callers (reducer + shell-preview) and is
  // byte-identical for every present-normal input.
  const extrusion: Vec3 = source.extrusion ?? { x: 0, y: 0, z: 1 };
  const { matrix: m, scaleZ, insertionZ, basePointZ } = params;

  // ── center XY (POSITION, DXF 10/20) through the full 2D affine ──
  const cx = m.a * center.x + m.c * center.y + m.e;
  const cy = m.b * center.x + m.d * center.y + m.f;

  // ── major axis (DIRECTION, DXF 11/21/31) — F3 ──
  // XY through the linear part (drop e/f); z scales purely by scaleZ. Kept
  // CONDITIONAL on a present source z so a planar ellipse stays byte-identical.
  const majorWorldX = m.a * majorAxisEndpoint.x + m.c * majorAxisEndpoint.y;
  const majorWorldY = m.b * majorAxisEndpoint.x + m.d * majorAxisEndpoint.y;
  const majorZ =
    majorAxisEndpoint.z !== undefined ? majorAxisEndpoint.z * scaleZ : undefined;
  const majorWorldZ = majorZ ?? 0;

  // ── minor axis (DIRECTION) → ratio — F5 ──
  // TRUE in-plane minor = extrusion × major, renormalised to ratio·|major|; then
  // transform BOTH axes by the full 3D linear part and recompute the ratio from
  // the FULL 3D lengths (see module doc for the exactness envelope + residual).
  const majorVecZ = majorAxisEndpoint.z ?? 0;
  const majorLen0 = Math.hypot(majorAxisEndpoint.x, majorAxisEndpoint.y, majorVecZ);
  // minorDir = extrusion × majorVec  (⊥ both ⇒ in-plane, ⊥ major)
  const minorDirX = extrusion.y * majorVecZ - extrusion.z * majorAxisEndpoint.y;
  const minorDirY = extrusion.z * majorAxisEndpoint.x - extrusion.x * majorVecZ;
  const minorDirZ = extrusion.x * majorAxisEndpoint.y - extrusion.y * majorAxisEndpoint.x;
  const minorDirLen = Math.hypot(minorDirX, minorDirY, minorDirZ);
  const minorScale = minorDirLen === 0 ? 0 : (ratio * majorLen0) / minorDirLen;
  const minorVecX = minorDirX * minorScale;
  const minorVecY = minorDirY * minorScale;
  const minorVecZ = minorDirZ * minorScale;
  const minorWorldX = m.a * minorVecX + m.c * minorVecY;
  const minorWorldY = m.b * minorVecX + m.d * minorVecY;
  const minorWorldZ = minorVecZ * scaleZ;
  const majorLen = Math.hypot(majorWorldX, majorWorldY, majorWorldZ);
  const minorLen = Math.hypot(minorWorldX, minorWorldY, minorWorldZ);

  // ── center world z (POSITION, DXF 30) — F4 + F7 ──
  const worldCenterZ = insertionZ + scaleZ * ((center.z ?? 0) - basePointZ);
  const outCenter: OptionalZPoint =
    center.z !== undefined || Math.abs(worldCenterZ) > 1e-12
      ? { x: cx, y: cy, z: worldCenterZ }
      : { x: cx, y: cy };

  // ── OCS extrusion NORMAL (DXF 210/220/230) — F6 + F8 ──
  // det(M)·M⁻ᵀ·n collapses (block-diagonal blockdiag(L, scaleZ)) to the 2×2
  // COFACTOR map with scaleZ on XY and det(L) on z. A degenerate image falls
  // back to the source normal.
  const detL = m.a * m.d - m.b * m.c;
  const rawNx = scaleZ * (m.d * extrusion.x - m.b * extrusion.y);
  const rawNy = scaleZ * (-m.c * extrusion.x + m.a * extrusion.y);
  const rawNz = detL * extrusion.z;
  const rawLen = Math.hypot(rawNx, rawNy, rawNz);
  const outExtrusion: Vec3 =
    rawLen < 1e-12
      ? { x: extrusion.x, y: extrusion.y, z: extrusion.z }
      : { x: rawNx / rawLen, y: rawNy / rawLen, z: rawNz / rawLen };

  // ── PRINCIPAL axes — F5 + F1(round-7) ──
  // `major′` (majorWorld*) and `minor′` (minorWorld*) are the images of two
  // ORTHOGONAL source semi-axes ⇒ CONJUGATE semi-diameters of the resulting
  // ellipse. DXF stores the PRINCIPAL major axis + minor/major ratio, so when the
  // images are non-orthogonal recover the principal axes via Rytz — the same
  // `abs(dot) > 1e-6` branch ezdxf `ConstructionEllipse.transform` uses.
  const dotN =
    majorLen > 1e-12 && minorLen > 1e-12
      ? (majorWorldX * minorWorldX +
          majorWorldY * minorWorldY +
          majorWorldZ * minorWorldZ) /
        (majorLen * minorLen)
      : 0;

  // The two transformed CONJUGATE semi-diameters (images of the orthogonal
  // source semi-axes). Reused below to re-anchor the arc params (F2) — the image
  // of the source vertex at param θ is `majorWorld·cos θ + minorWorld·sin θ`.
  const majorWorld: Vec3 = { x: majorWorldX, y: majorWorldY, z: majorWorldZ };
  const minorWorld: Vec3 = { x: minorWorldX, y: minorWorldY, z: minorWorldZ };

  let principalMajor: Vec3;
  let principalMinor: Vec3;
  let outRatio: number;
  let principalExtrusion: Vec3;
  let adjustParams: boolean;
  // ── F2 (round-10) edge-on projection collapse ──
  // The PDF orthographic projection drives this helper with `scaleZ:0` (drop world
  // z). An edge-on ellipse whose MAJOR axis is (near) parallel to the dropped z maps
  // its major to ~0 length while the in-plane MINOR survives. The principal-axis
  // branches below would then keep a zero-length major (ellipseToBeziers renders a
  // POINT — the surviving minor is LOST) or, for a tiny-but-nonzero major, feed the
  // numerically-unstable `ratio = minor/major → ∞` longest-axis swap (NaN risk). A
  // RELATIVE test (major negligible vs the ellipse scale) catches both exact-0 and
  // float-noise collapse without misfiring on a legitimately thin ellipse (small
  // MINOR, large major — handled by the else branch's `ratio ≈ 0`).
  const axisScale = Math.max(majorLen, minorLen);
  const majorCollapsed = axisScale > 1e-12 && majorLen < axisScale * 1e-9;
  let promotedMinor = false;
  if (axisScale <= 1e-12) {
    // BOTH conjugate diameters collapsed → the projection is a POINT. Emit a finite,
    // degenerate ellipse (zero major, ratio 0); ellipseToBeziers renders the point.
    principalMajor = ZERO_VEC;
    principalMinor = ZERO_VEC;
    outRatio = 0;
    principalExtrusion = outExtrusion;
    adjustParams = false;
  } else if (majorCollapsed) {
    // PROMOTE the surviving minor to the principal major — a valid THIN ellipse (a
    // line), never a normalized zero-length major. Mirrors the ezdxf longest-axis
    // swap (relabel minor→major, invert the ratio to ≈0, shift an open arc's params
    // −90° below so they stay anchored to the promoted major).
    principalMajor = minorWorld;
    principalMinor = majorWorld; // ≈ 0 (the collapsed axis) — not read on output
    outRatio = majorLen / minorLen; // ≈ 0 → thin ellipse / line, no divide-by-zero
    principalExtrusion = outExtrusion;
    adjustParams = false;
    promotedMinor = true;
  } else if (Math.abs(dotN) > 1e-6) {
    // Non-orthogonal conjugate diameters → Rytz principal-axis construction.
    const rytz = rytzAxisConstruction(majorWorld, minorWorld);
    principalMajor = rytz.major;
    principalMinor = rytz.minor;
    outRatio = rytz.ratio;
    // ezdxf recomputes the plane normal from the PRINCIPAL pair (major × minor):
    // keeps the (major, minor, normal) triple mutually consistent AND lets a
    // mirror's flipped handedness fall out of the cross. Fall back to the
    // cofactor normal if the recovered pair is degenerate.
    principalExtrusion = scaleToLength(
      cross(rytz.major, rytz.minor),
      1,
      outExtrusion,
    );
    // The principal frame ROTATED relative to the source major axis, so an open
    // arc's params must be re-anchored (ezdxf `adjust_params = True`).
    adjustParams = true;
  } else {
    // Already-principal (orthogonal) image — uniform scale (conformal) or an
    // axis-aligned major axis. Keep the transformed major + cofactor normal
    // BYTE-IDENTICAL to the pre-round-7 path (no Rytz float noise).
    principalMajor = majorWorld;
    outRatio = majorLen === 0 ? ratio : minorLen / majorLen;
    principalExtrusion = outExtrusion;
    // Exact minor axis (ezdxf transform orthogonal branch: `new_minor_axis =
    // minor_axis(new_major, new_extrusion, new_ratio)`). Only consumed by the
    // ratio>1 axis swap below — the byte-identical keep output never reads it.
    principalMinor = minorAxisFrom(principalMajor, principalExtrusion, outRatio);
    // Frame preserved (major stays the transformed major) ⇒ params unchanged
    // unless the ratio>1 swap fires (ezdxf `adjust_params = False`).
    adjustParams = false;
  }

  // ── Arc param re-anchor (F2) + longest-axis swap (F1) ──
  // Faithful port of the ezdxf `ConstructionEllipse.transform` param tail: an
  // OPEN arc whose principal frame rotated (Rytz) re-measures its start/end in
  // the recovered frame; a recovered minor LONGER than the major (`ratio > 1`)
  // is invalid DXF (major must be the LONGEST semi-axis, `ratio ≤ 1`), so the
  // axes are swapped and the params shift −90°. A FULL ellipse (no params) and
  // every frame-preserving case leave the params BYTE-IDENTICAL (untouched).
  const hasParams =
    source.startParam !== undefined && source.endParam !== undefined;
  let s = source.startParam ?? 0;
  let e = source.endParam ?? 0;
  let paramsChanged = false;

  if (hasParams && adjustParams && !isCloseNum(s, e)) {
    // Re-measure each param in the PRE-SWAP recovered principal frame. ezdxf's
    // `param(vec)` divides the minor projection by the ratio ("adjust to circle")
    // so an ellipse param — not a mere geometric angle — comes out.
    const xh = normalize(principalMajor);
    const yh = normalize(principalMinor);
    const paramOf = (param: number): number => {
      const c = Math.cos(param);
      const sn = Math.sin(param);
      const v: Vec3 = {
        x: majorWorld.x * c + minorWorld.x * sn,
        y: majorWorld.y * c + minorWorld.y * sn,
        z: majorWorld.z * c + minorWorld.z * sn,
      };
      return modTau(Math.atan2(dot3(yh, v) / outRatio, dot3(xh, v)));
    };
    s = paramOf(source.startParam!);
    e = paramOf(source.endParam!);
    // ezdxf side-of-curve correction: the CCW span must survive the re-anchor.
    //
    // The re-anchored pair describes ONE of two complementary arcs — `(s, e)`
    // or `(e, s)` — and this picks the one that is REALLY the image of the
    // source arc, by MID-ARC POINT: the image of the source arc's mid-arc point
    // is the result arc's mid-arc point, so the candidate whose own mid-arc
    // point is NEARER to it is the right one.
    //
    // NEAREST-OF-TWO, not a threshold (@codex round 18). ezdxf compares the CCW
    // SPAN first and only falls back to this point test for an exact-π span
    // (where both orderings span π), and this port spelled both comparisons as
    // FIXED absolute `1e-9` tests. Both quantities carry the Rytz recovery's own
    // conditioning, whose relative error grows as `eps / |û·v̂|` as the mapped
    // conjugate pair approaches orthogonality, so inside that band a fixed
    // tolerance is a coin flip — and a wrong flip draws the arc on the
    // COMPLEMENTARY half, which is 100% wrong geometry rather than a rounding
    // artefact. Measured: a tilted `(0.022, 0.616) →(bulge +1)→ (1, 0.342)`
    // semicircle under `scale (8.03, 50)` had its mid-arc point drawn at
    // `(6.20, 0.068, 0.968)` where the affine image puts it at
    // `(4.00, 0.052, −0.010)`, because the two candidate mid-arc points agreed
    // only to 5.2e-9 on axes 4.1 long.
    //
    // The point test also subsumes the span test rather than replacing it with
    // something weaker: the span discriminates only when the two spans differ,
    // while the two mid-arc points differ for EVERY open arc of a non-degenerate
    // ellipse (they are antipodal about the centre). Outside the ill-conditioned
    // band both give the same answer — the matching candidate agrees to float
    // noise while the other is a whole arc away — so every well-conditioned case
    // is unchanged, which is what `./ellipseExplodeGeometry.test.ts`' ezdxf
    // oracles B/C/D pin.
    const midOld = midParam(source.startParam!, source.endParam!);
    const co = Math.cos(midOld);
    const so = Math.sin(midOld);
    const oldChk: Vec3 = {
      x: majorWorld.x * co + minorWorld.x * so,
      y: majorWorld.y * co + minorWorld.y * so,
      z: majorWorld.z * co + minorWorld.z * so,
    };
    const midPointGap = (from: number, to: number): number => {
      const mid = midParam(from, to);
      const cn = Math.cos(mid);
      const sn = Math.sin(mid);
      return Math.hypot(
        principalMajor.x * cn + principalMinor.x * sn - oldChk.x,
        principalMajor.y * cn + principalMinor.y * sn - oldChk.y,
        principalMajor.z * cn + principalMinor.z * sn - oldChk.z,
      );
    };
    // A TIE cannot arise for a non-degenerate ellipse: `oldChk` IS one of the
    // two candidates up to float noise and they are antipodal about the centre,
    // so one gap is ~0 and the other ~their whole separation. The
    // `sideOfCurve` property pins exactly that margin (chosen gap ≤ ¼ of the
    // separation, whenever the separation exceeds 1e-6 of the major axis), which
    // is why `<` vs `<=` here is unobservable — see that file. The `<=`
    // mutant accordingly SURVIVES the targeted run and is equivalent;
    // deliberately NOT suppressed, because a per-mutator `Stryker disable
    // next-line EqualityOperator` would also hide this line's KILLABLE `>=`
    // twin (the same triage `blockLinearTransform.ts::invertBlockLinear`
    // carries).
    if (midPointGap(e, s) < midPointGap(s, e)) {
      const tmp = s;
      s = e;
      e = tmp;
    }
    paramsChanged = true;
  }

  // Longest-axis swap — ezdxf `if new_ratio > 1:` (applies to BOTH branches):
  // the recovered minor is the longer semi-axis, so relabel it the major (via
  // the exact `minor_axis` cross so the new major is ⊥ the old + correctly
  // scaled), invert the ratio, and — for an OPEN arc that is NOT a full turn —
  // shift the params −90° so they stay anchored to the (rotated) major axis.
  if (outRatio > 1) {
    principalMajor = minorAxisFrom(principalMajor, principalExtrusion, outRatio);
    outRatio = 1 / outRatio;
    principalMinor = minorAxisFrom(principalMajor, principalExtrusion, outRatio);
    if (hasParams && !(isCloseNum(s, 0) && isCloseNum(e, TAU))) {
      s -= HALF_PI;
      e -= HALF_PI;
      paramsChanged = true;
    }
  }

  // F2 (round-10) — promoted-minor param re-anchor. Relabeling the surviving minor
  // as the principal major (the edge-on collapse above) shifts an OPEN arc's params
  // −90°, exactly as the ezdxf longest-axis swap does — so the drawn portion of the
  // projected line stays anchored to the promoted major. A FULL ellipse (0..2π) is
  // untouched (the projected line is traced in full regardless of anchoring).
  if (
    promotedMinor &&
    hasParams &&
    !(isCloseNum(s, 0) && isCloseNum(e, TAU))
  ) {
    s -= HALF_PI;
    e -= HALF_PI;
    paramsChanged = true;
  }

  let outStart = source.startParam;
  let outEnd = source.endParam;
  if (paramsChanged) {
    s = modTau(s);
    e = modTau(e);
    // A degenerate span after re-anchor collapses to a full ellipse (ezdxf).
    if (isCloseNum(s, e)) {
      s = 0;
      e = TAU;
    }
    outStart = s;
    outEnd = e;
  }

  // Major-axis z-omission (F3): omit the optional major z ONLY when the result
  // is genuinely PLANAR — the source major carried no z AND the recovered
  // principal major has no z either. A rotated non-uniform INSERT can enter the
  // Rytz branch (or the ratio>1 swap) and recover a principal major carrying a
  // REAL z even when the source DXF-31 major z was 0/omitted (a source whose
  // tilt lives entirely in the OCS normal); that z is genuine and MUST survive.
  const zIntroduced = Math.abs(principalMajor.z) > 1e-9;
  const outMajor: OptionalZPoint =
    majorZ !== undefined || zIntroduced
      ? { x: principalMajor.x, y: principalMajor.y, z: principalMajor.z }
      : { x: principalMajor.x, y: principalMajor.y };

  return {
    center: outCenter,
    majorAxisEndpoint: outMajor,
    ratio: outRatio,
    extrusion: principalExtrusion,
    extrusionIsDefault: isDefaultNormal(principalExtrusion),
    ...(hasParams ? { startParam: outStart, endParam: outEnd } : {}),
  };
}
